/**
 * Airtime Machine — top-ups, adjustments, sales deductions, running balance
 * and monthly accountant statement. Moved here from Cash-Up per the store
 * request ("keep the Cash-Up screen focused on the till, put the airtime
 * ledger under Daily Operations").
 *
 * The widget talks to the same `/api/airtime-machine/*` endpoints, so no
 * server work was needed for the move.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Smartphone, RefreshCw, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `R ${Number(v || 0).toFixed(2)}`;

export default function AirtimeMachine() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [topupAmt, setTopupAmt] = useState("");
  const [topupNote, setTopupNote] = useState("");
  const [adjAmt, setAdjAmt] = useState("");
  const [adjReason, setAdjReason] = useState("");
  const now = new Date();
  const [stmtYear, setStmtYear] = useState(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear());
  const [stmtMonth, setStmtMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth());
  const [stmtBusy, setStmtBusy] = useState(false);

  const fetchBalance = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/airtime-machine/balance`);
      setData(r.data);
    } catch {
      toast.error("Failed to load airtime balance");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBalance(); }, []);

  const addTopup = async () => {
    if (!topupAmt || Number(topupAmt) <= 0) return toast.error("Enter a valid amount");
    try {
      await axios.post(`${API}/airtime-machine/topup`, {
        amount: Number(topupAmt),
        date: new Date().toISOString().split("T")[0],
        note: topupNote || "EFT top-up",
      });
      toast.success(`R ${topupAmt} top-up recorded`);
      setTopupAmt(""); setTopupNote("");
      fetchBalance();
    } catch { toast.error("Failed to add top-up"); }
  };

  const addAdjustment = async () => {
    if (!adjAmt || !adjReason) return toast.error("Amount and reason required");
    try {
      await axios.post(`${API}/airtime-machine/adjustment`, {
        amount: Number(adjAmt),
        date: new Date().toISOString().split("T")[0],
        reason: adjReason,
      });
      toast.success("Adjustment recorded");
      setAdjAmt(""); setAdjReason("");
      fetchBalance();
    } catch { toast.error("Failed to record adjustment"); }
  };

  const deleteTopup = async (id) => {
    if (!window.confirm("Delete this top-up?")) return;
    try {
      await axios.delete(`${API}/airtime-machine/topup/${id}`);
      toast.success("Top-up removed");
      fetchBalance();
    } catch { toast.error("Failed to delete"); }
  };

  const deleteAdjustment = async (id) => {
    try {
      await axios.delete(`${API}/airtime-machine/adjustment/${id}`);
      toast.success("Adjustment removed");
      fetchBalance();
    } catch { toast.error("Failed to delete"); }
  };

  const downloadStatementPdf = async () => {
    setStmtBusy(true);
    try {
      const res = await axios.get(
        `${API}/airtime-machine/statement/pdf?year=${stmtYear}&month=${stmtMonth}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `airtime-topups-${stmtYear}-${String(stmtMonth).padStart(2, "0")}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
      toast.success("Statement downloaded");
    } catch { toast.error("Failed to download"); }
    finally { setStmtBusy(false); }
  };

  const emailStatement = async () => {
    const to = window.prompt("Email statement to:", "");
    if (to === null) return;
    setStmtBusy(true);
    try {
      await axios.post(`${API}/email/airtime-statement`, {
        year: stmtYear, month: stmtMonth,
        recipient_email: (to || "").trim() || undefined,
      });
      toast.success("Statement emailed");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to email"); }
    finally { setStmtBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="airtime-machine-page">
      <Card>
        <CardHeader className="bg-gradient-to-r from-cyan-600 to-cyan-700 text-white py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Smartphone className="h-5 w-5" />
              <div>
                <CardTitle className="text-lg">Airtime Machine Balance</CardTitle>
                <p className="text-cyan-100 text-sm">Top-ups from bank · sales deductions · running balance</p>
              </div>
            </div>
            <Button variant="secondary" size="sm" onClick={fetchBalance} disabled={loading} data-testid="airtime-refresh">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          {!data ? (
            <p className="text-center py-8 text-gray-500">Loading…</p>
          ) : (
            <>
              {/* Balance summary */}
              <div className={`p-4 rounded-lg text-center ${
                data.alert_level === "critical" ? "bg-red-100 border border-red-300" :
                data.alert_level === "warning" ? "bg-amber-100 border border-amber-300" :
                "bg-green-100 border border-green-300"
              }`}>
                <div className="text-3xl font-bold">{fmt(data.balance)}</div>
                <div className="text-xs text-gray-600 mt-1">Current balance</div>
              </div>

              {/* Add top-up */}
              <div className="border rounded-lg p-3">
                <p className="font-semibold text-sm mb-2">Record Bank Top-up</p>
                <div className="flex gap-2 flex-wrap">
                  <Input type="number" placeholder="Amount" value={topupAmt} onChange={(e) => setTopupAmt(e.target.value)} className="w-32" data-testid="airtime-topup-amount" />
                  <Input placeholder="Note (optional)" value={topupNote} onChange={(e) => setTopupNote(e.target.value)} className="flex-1" data-testid="airtime-topup-note" />
                  <Button onClick={addTopup} data-testid="airtime-add-topup">Add Top-up</Button>
                </div>
              </div>

              {/* Add adjustment */}
              <div className="border rounded-lg p-3">
                <p className="font-semibold text-sm mb-2">Manual Adjustment</p>
                <div className="flex gap-2 flex-wrap">
                  <Input type="number" placeholder="Amount (+/-)" value={adjAmt} onChange={(e) => setAdjAmt(e.target.value)} className="w-32" data-testid="airtime-adj-amount" />
                  <Input placeholder="Reason" value={adjReason} onChange={(e) => setAdjReason(e.target.value)} className="flex-1" data-testid="airtime-adj-reason" />
                  <Button variant="outline" onClick={addAdjustment} data-testid="airtime-add-adj">Record</Button>
                </div>
              </div>

              {/* Transactions list */}
              <div className="border rounded-lg p-3">
                <p className="font-semibold text-sm mb-2">Recent Activity</p>
                <div className="max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-100 text-left">
                      <tr><th className="px-3 py-1.5">Date</th><th className="px-3 py-1.5">Type</th><th className="px-3 py-1.5">Note</th><th className="px-3 py-1.5 text-right">Amount</th><th></th></tr>
                    </thead>
                    <tbody>
                      {(data.topups || []).map((t) => (
                        <tr key={`t-${t.id}`} className="border-b hover:bg-cyan-50">
                          <td className="px-3 py-1.5">{t.date}</td>
                          <td className="px-3 py-1.5"><span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded">Top-up</span></td>
                          <td className="px-3 py-1.5 text-gray-600">{t.note}</td>
                          <td className="px-3 py-1.5 text-right font-semibold text-cyan-700">+{fmt(t.amount)}</td>
                          <td className="px-3 py-1.5"><button onClick={() => deleteTopup(t.id)} className="text-gray-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button></td>
                        </tr>
                      ))}
                      {(data.adjustments || []).map((a) => (
                        <tr key={`a-${a.id}`} className="border-b hover:bg-purple-50">
                          <td className="px-3 py-1.5">{a.date}</td>
                          <td className="px-3 py-1.5"><span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Adjust</span></td>
                          <td className="px-3 py-1.5 text-gray-600">{a.reason}</td>
                          <td className={`px-3 py-1.5 text-right font-semibold ${Number(a.amount) < 0 ? "text-red-600" : "text-purple-700"}`}>{fmt(a.amount)}</td>
                          <td className="px-3 py-1.5"><button onClick={() => deleteAdjustment(a.id)} className="text-gray-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button></td>
                        </tr>
                      ))}
                      {(data.recent_sales || []).slice(0, 20).map((s, i) => (
                        <tr key={`s-${i}`} className="border-b hover:bg-orange-50">
                          <td className="px-3 py-1.5">{s.date}</td>
                          <td className="px-3 py-1.5"><span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Sale</span></td>
                          <td className="px-3 py-1.5 text-gray-600">{s.item} ({s.transaction})</td>
                          <td className="px-3 py-1.5 text-right font-semibold text-orange-700">-{fmt(s.amount)}</td>
                          <td></td>
                        </tr>
                      ))}
                      {!data.topups?.length && !data.adjustments?.length && !data.recent_sales?.length && (
                        <tr><td colSpan="5" className="text-center py-4 text-gray-400">No transactions yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Monthly statement for accountant */}
              <div className="border rounded-lg p-3 bg-gradient-to-r from-slate-50 to-cyan-50" data-testid="airtime-monthly-statement">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold text-sm">Monthly Statement for Accountant</p>
                  <span className="text-xs text-slate-500">Bank top-ups only — for reconciliation</span>
                </div>
                <div className="flex gap-2 items-end flex-wrap">
                  <div>
                    <Label className="text-xs">Month</Label>
                    <select className="border rounded px-2 py-1.5 text-sm bg-white" value={stmtMonth} onChange={(e) => setStmtMonth(Number(e.target.value))} data-testid="airtime-stmt-month">
                      {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
                        <option key={i} value={i + 1}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Year</Label>
                    <select className="border rounded px-2 py-1.5 text-sm bg-white" value={stmtYear} onChange={(e) => setStmtYear(Number(e.target.value))} data-testid="airtime-stmt-year">
                      {Array.from({ length: 4 }, (_, i) => now.getFullYear() - i).map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                  <Button onClick={downloadStatementPdf} disabled={stmtBusy} data-testid="airtime-stmt-download-btn">
                    {stmtBusy ? "Preparing…" : "Download PDF"}
                  </Button>
                  <Button variant="outline" onClick={emailStatement} disabled={stmtBusy} data-testid="airtime-stmt-email-btn">
                    Email to Accountant
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
